/**
 * A wrapper component to enable create a new track window or edit an existing one
 * @description 
 *  Edit if props.data is provided, and it has to be a TrackWindow type.
 *  Otherwise, it will create a new track window
 */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { ToastsStore } from '@bnsf/bnsf-react-toast-messages';
import keyMirror from '../../../utils/keyMirror';
import YesNoModal from '../../Modals/YesNoModal';
import _ from 'underscore';
import { setDataReload } from '../../../redux/actions/setDataReload';

import ActionTypes from './constants';

import WinEditStep1 from './WinEditStep1';
import WinEditStep2 from './WinEditStep2';
import WinEditStep3 from './WinEditStep3';

import ViolationModal from '../../GuidelineViolation/ViolationModal';

import './WindowEditView.css';
import TrackWindow from '../../../model/TrackWindow';
import { createWindowRequest, fetchWindowFlocs, updateWindowRequest, validateWindowRequest } from '../../../services/trackWindow';

const MODE = keyMirror({
    NEW_WINDOW: null,
    EDIT_WINDOW: null
});

const steps = ['', '', ''];

const WindowEditView = (props) => {
    // prompt the user if switch to other view after making some changes
    const [ activeStep, setActiveStep ] = useState(0);
    const [ mode, setMode ] = useState(MODE.NEW_WINDOW);
    const [ editWin, setEditWin ] = useState(new TrackWindow());
    const [ origWin, setOrigWin ] = useState(null);
    const [ warnUser, setWarnUser ] = useState(false);
    const [ showViolations, setShowViolations ] = useState(false);
    const [ violations, setViolations ] = useState([]);
    const [ submitStatus, setSubmitStatus ] = useState('');
  
    useEffect(() => {
        if (props.data instanceof TrackWindow) {
            setEditWin(props.data.clone());
            setOrigWin(props.data.clone());
            setMode(MODE.EDIT_WINDOW);
        } else {    // create a new window
            setEditWin(new TrackWindow());
            setOrigWin(new TrackWindow());
            setMode(MODE.NEW_WINDOW);
        }

        // reset for WinEditStep2
        props.setTrackOptions([]);
        props.setSelectedTrackItems([]);
        props.setClicNums('');

    }, [props.view]);

    useEffect(()=>{       
        console.log("Mode:", mode)
    }, [])

    const onNext = () => {  
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const onBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const onCancel = () => {
        if (isDirty()) {
            setWarnUser(true);
        } else {
            props.switchView(props.view.previous);
        }
    }

    const onSubmit = () => {
        setSubmitStatus('ON_SUBMIT_START');

        console.log(mode === MODE.NEW_WINDOW ? "Creating window..." : "Updating window...");
        console.log(editWin.toJson());
        
        // Business Requirement: skip guideline validation for Terminal Windows
        if (editWin.winType === 'TW') {
            proceedSave();
        } else {
            // validate window against division guidelines
            validateWindowRequest(editWin).then(onValidationSuccess, onValidationFailure);
        }        
    }

    const proceedSave = () => {
        fetchWindowFlocs(editWin).then((resp)=> onFetchWindowFlocsSuccess(resp, true), onFetchWindowFlocsFailure);
    }

    // Violation History: for intermediate windows
    const proceedIntermediateWinSave = () => {
        fetchWindowFlocs(editWin).then((resp)=> onFetchWindowFlocsSuccess(resp, false), onFetchWindowFlocsFailure);
    }

    const onFetchWindowFlocsSuccess = (resp, proceed) => {
        let flocs = processFlocsDetails(resp);
        editWin.flocs = flocs;
        editWin.violations = violations;
        editWin.isFinal = proceed === true;
        processWindowRequest();       
    }

    const onFetchWindowFlocsFailure = (err) => {
        setSubmitStatus('ON_SUBMIT_FAILURE');

        ToastsStore.showToast("FETCH_WINDOW_FLOCS_FAILURE");
        console.error(err);
    }

    const processFlocsDetails = (resp) => {
        let flocs = [];
        if (resp && resp.length > 0) {
            resp.forEach(aWin => {
                flocs.push({
                    flocId: aWin.flocId,
                    rotation: aWin.rotation,
                    isCritical: aWin.critical,
                });
            });
        }
        
        return flocs;
    }

    const processWindowRequest = () => {
      if (mode === MODE.NEW_WINDOW) {
        createWindowRequest(editWin, props.userProfile).then(onCreateSuccess, onCreateFailure).finally(onSubmitFinal);
      } else if (mode === MODE.EDIT_WINDOW) {
        updateWindowRequest(editWin, props.userProfile).then(onUpdateSuccess, onUpdateFailure).finally(onSubmitFinal);
      }
    };

    const onSubmitFinal = () => setSubmitStatus('ON_SUBMIT_END');

    const onValidationSuccess = (resp) => {
        if (resp.windowValid) {
            proceedSave();
        }
        else {
            setViolations(resp.violations);
            setShowViolations(true);                
        }
    }

    const onValidationFailure = (err) => {
        setSubmitStatus('ON_SUBMIT_FAILURE');

        ToastsStore.showToast("VALIDATION_FAILURE");
        console.error(err);
    }

    const onCreateSuccess = (resp) => {
        switch (resp.returnCode) {
            case '0':
                if (editWin.isFinal) {
                    console.info("Window created successfully!");
                    props.setDataReload(true);
                    props.switchView(props.view.previous);
    
                    ToastsStore.showToast("CREATE_SUCCESS");
                    break;
                } else {
                    console.info("Intermediate Window created successfully!");
                    let newIntermediateWin = new TrackWindow();
                    newIntermediateWin.fromCreateUpdateService(resp.twmmodelBWWP[0].trackWindowReq);
                    setEditWin(newIntermediateWin);
                    setMode(MODE.EDIT_WINDOW);
                    setViolations([]);
                    break;
                }
               
            case '2':
                console.error("Failed to create the window!");
                console.log(resp);

                ToastsStore.showToast("CREATE_FAILURE");
                break;
            default:
                console.warn("createWindow: unrecognized return code");
        }
    }

    const onCreateFailure = () => {
        ToastsStore.showToast("CREATE_FAILURE");
    }

    const onUpdateSuccess = (resp) => {
        switch (resp.returnCode) {
            case '0':
                if (editWin.isFinal) {
                    console.info("Window updated successfully!");
                    props.setDataReload(true);
                    props.switchView(props.view.previous);
                    ToastsStore.showToast("UPDATE_SUCCESS");
                    break;
                } else {
                    console.info("Intermediate Window updated successfully!");
                    let newIntermediateWin = new TrackWindow();
                    newIntermediateWin.fromCreateUpdateService(resp.twmmodelBWWP[0].trackWindowReq);
                    setEditWin(newIntermediateWin);
                    setMode(MODE.EDIT_WINDOW);
                    setViolations([]);
                    break;
                }
            case '2':
                console.error("Failed to update the Window!");
                console.log(resp);

                ToastsStore.showToast("UPDATE_FAILURE");
                break;
            default:
                console.warn("updateWindow: unrecognized return code");
        }
    }

    const onUpdateFailure = () => {
        ToastsStore.showToast("UPDATE_FAILURE");
    }

    const onYes_cancelModal = () => {
        setWarnUser(false);
        props.switchView(props.view.previous);
    }

    const onNo_cancelModal = () => {
        setWarnUser(false);
    }

    const updateWin = (o) => {
        /**
         * Business Requirement: when window type changed to Terminal Window (TW) from other type, OR 
         * changed from other type to Terminal Window (TW), empty out LS, BMP, EMP. Track Type, and CLIC 
         * numbers, to enforce the user reenter all those location attributes.
         */
        if ('winType' in o && o.winType !== editWin.winType && [o.winType, editWin.winType].includes('TW')) {
            Object.assign(o, {
                lineSegment: '',
                bmp: '',
                bmpSuf: '',
                emp: '',
                empSuf: '',
                bcp: '',
                ecp: '',
                trackList: [],
                trackType: '',
                trackNum: ''
            });

            props.setTrackOptions([]);
            props.setSelectedTrackItems([]);
            props.setClicNums('');
        }

        Object.entries(o).map(([key, value]) => editWin[key] = value);
    }

    const isDirty = () => {
        return !(_.isEqual(origWin, editWin));
    }

    return (
        <div className='clsWinEditWrapper'>           
            <div className='clsStepper'>
                {steps.map((label, index) => {
                    const classes = activeStep === index ? 'clsStepLabel active' :
                            activeStep > index ? 'clsStepLabel completed' : 'clsStepLabel';
                    return (
                        <React.Fragment key={index}>
                            {index > 0 && <div className='clsStepConnector' />}
                            <div className={classes}>
                                <div className='clsStepCircle' />
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>

            { activeStep === 0 ? 
                    <WinEditStep1 data = { editWin } 
                        onCancel = { onCancel } 
                        onNext = { onNext } 
                        updateWin = { updateWin } /> 
                : activeStep === 1 ? 
                    <WinEditStep2 
                        data = { editWin } 
                        origData = { origWin }
                        onCancel = { onCancel } 
                        onBack = { onBack } 
                        onNext = { onNext } 
                        updateWin = { updateWin } /> 
                    : <WinEditStep3 data = { editWin } 
                            onCancel = { onCancel } 
                            onBack = { onBack } 
                            onSubmit = { onSubmit }
                            updateWin = { updateWin } 
                            submitStatus = { submitStatus } /> }
            
            { warnUser ? <YesNoModal title='WARNING'
                message = 'The window changes will be discarded. Do you want to continue? Click YES to proceed; otherwise, click NO' 
                onNo = { onNo_cancelModal } 
                onYes = { onYes_cancelModal } /> : null }
            
            { showViolations ? <ViolationModal violations = { violations } onProceed = { proceedSave } onClose = { onSubmitFinal } /> : null }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        userProfile: state.oidc.user && state.oidc.user.profile,
	    view: state.view
    };
}

const mapDispatchToProps = dispatch => {
    return {
        setDataReload: (reload) => dispatch(setDataReload(reload)),
        setTrackOptions: (trkOps) => dispatch({ type: ActionTypes.SET_TRACK_OPTIONS, trackOptions: trkOps }),
        setSelectedTrackItems: (trkItems) => dispatch({ type: ActionTypes.SET_SELECTED_TRACK_ITEMS, selectedTrackItems: trkItems }),
        setClicNums: (clics) => dispatch({ type: ActionTypes.SET_CLIC_NUMS, clicNums: clics })
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(WindowEditView);